<script lang="ts">
  import InlineSVG from 'svelte-inline-svg';

  import {
    DevelopmentProjectURLTypes,
    ConsultingProjectURLTypes,
    ProjectType
  } from '../../types/project';

  import {urlRegex} from '../../constants/urls';

  import addIcon from '../../assets/icons/add.svg';
  import removeIcon from '../../assets/icons/circle-with-cross.svg';

  import IconButton from '../icon-button/IconButton.svelte';

  export let label = '';
  export let typePlaceholder = '';
  export let urlPlaceholder = '';
  export let urls = [{type: '', url: ''}];
  export let id = '';
  export let required = false;
  export let projectType: ProjectType;

  $: typeOptions = [
    ...Object.values(DevelopmentProjectURLTypes),
    ...(projectType === ProjectType.CONSULTING
      ? Object.values(ConsultingProjectURLTypes)
      : [])
  ].map(type => ({
    value: type,
    label: type
  }));

  let validationErrors: Array<string | null> = [];

  // Validate URLs on input blur and type change
  function validateUrls() {
    validationErrors = urls.map(({type, url}): string | null => {
      if (url && !urlRegex.test(url)) {
        return 'Please enter a valid URL.';
      }
      if (url && !type) {
        return 'Please select a type.';
      }
      return null;
    });
  }

  function handleAdd() {
    urls = urls.concat({type: '', url: ''});
  }
</script>

<style lang="postcss">
  .container {
    margin-bottom: 1.5rem;
  }

  .row {
    display: grid;
    grid-template-columns: 1fr 2rem;
    gap: 0.25rem;
    margin-bottom: 0.75rem;
  }

  @media (min-width: 768px) {
    .row {
      grid-template-columns: 1fr 2fr 1fr;
      align-items: start;
      gap: 1.5rem;
    }
  }

  .label {
    grid-column-start: 1;
    grid-column-end: 3;
    display: flex;
    align-items: center;
  }

  @media (min-width: 768px) {
    .label {
      grid-column-end: 1;
      justify-content: end;
      min-height: var(--ubilabs-input-height);
    }
  }

  .inputGroup {
    position: relative;
    display: grid;
    gap: 0.25rem 0.5rem;
  }

  @media (min-width: 768px) {
    .inputGroup {
      grid-template-columns: 1fr 2fr;
      grid-column: 2;
    }
  }

  .inputGroup > input {
    width: 100%;
  }

  .inputGroup + :global(button) {
    margin: calc(
        (var(--ubilabs-input-height) - var(--ubilabs-icon-button-height)) / 2
      )
      0;
  }

  @media (min-width: 768px) {
    .inputGroup + :global(button) {
      margin-left: -1.25rem;
    }
  }

  .errorMessage {
    display: block;
    color: var(--ubilabs-error-color);
  }

  @media (min-width: 768px) {
    .errorMessage {
      grid-column: 2;
    }
  }
</style>

<div class="container">
  <div class="row">
    <div class="label">
      {#if label}
        <label for={`${id}-0`}>{label}{required ? '*' : ''}</label>
        <IconButton on:click={handleAdd}>
          <InlineSVG src={addIcon} />
        </IconButton>
      {/if}
    </div>

    <div class="inputGroup">
      <select bind:value={urls[0].type} onChange={validateUrls}>
        <option value="">{typePlaceholder}</option>
        {#each typeOptions as option}
          <option value={option.value}>{option.label}</option>
        {/each}
      </select>
      <input
        {required}
        type="text"
        placeholder={urlPlaceholder}
        name={`${id}-0`}
        id={`${id}-0`}
        bind:value={urls[0].url}
        on:blur={validateUrls} />

      {#if validationErrors[0]}
        <small class="errorMessage">{validationErrors[0]}</small>
      {/if}
    </div>

    {#if urls[0].type || urls[0].url}
      <IconButton
        on:click={() => {
          urls =
            urls.length > 1
              ? urls.slice(1, urls.length)
              : [{type: '', url: ''}];
        }}>
        <InlineSVG src={removeIcon} />
      </IconButton>
    {/if}
  </div>

  {#each urls as url, index}
    {#if index > 0}
      <div class="row">
        <div class="inputGroup">
          <select bind:value={url.type} onChange={validateUrls}>
            <option value="">{typePlaceholder}</option>
            {#each typeOptions as option}
              <option value={option.value}>{option.label}</option>
            {/each}
          </select>
          <input
            type="text"
            placeholder={urlPlaceholder}
            id={`${id}-${index}`}
            bind:value={url.url}
            on:blur={validateUrls} />

          {#if validationErrors[index]}
            <small class="errorMessage">{validationErrors[index]}</small>
          {/if}
        </div>

        <IconButton
          on:click={() => {
            urls = [
              ...urls.slice(0, index),
              ...urls.slice(index + 1, urls.length)
            ];
            validationErrors = [
              ...validationErrors.slice(0, index),
              ...validationErrors.slice(index + 1, validationErrors.length)
            ];
          }}>
          <InlineSVG src={removeIcon} />
        </IconButton>
      </div>
    {/if}
  {/each}
</div>
